import { useCallback, useState } from "react";

import {
  DEFAULT_HANDLER_SCOPE,
  type HandlerScope,
  handlerScopeParam,
} from "../types/handler";
import { useHandlers } from "./useHandlers";

/**
 * Which screen's filter this is.
 *
 * Stored per screen rather than once for the whole app, because the four
 * screens are four jobs. A dispatcher reviewing the morning's intake wants the
 * unclaimed queue; the same person planning the week wants their own containers;
 * and carrying one choice into the other means every screen opens on a filter
 * set for a different task. Four keys cost nothing and each remembers what its
 * own screen was last doing.
 */
export type HandlerScopeScreen =
  | "intake"
  | "orders"
  | "planning"
  | "containers";

const storageKey = (screen: HandlerScopeScreen) => `handlerScope:${screen}`;

/**
 * Read the stored choice, falling back to Everyone.
 *
 * Wrapped because `localStorage` throws outright in a few real
 * configurations — Safari's private mode, a locked-down corporate profile —
 * and a view preference must never be the thing that stops a page rendering.
 */
const readStored = (screen: HandlerScopeScreen): HandlerScope => {
  try {
    const stored = localStorage.getItem(storageKey(screen));
    return (stored as HandlerScope) || DEFAULT_HANDLER_SCOPE;
  } catch {
    return DEFAULT_HANDLER_SCOPE;
  }
};

export interface UseHandlerScope {
  scope: HandlerScope;
  setScope: (next: HandlerScope) => void;
  /**
   * The scope as the API takes it, or `undefined` for "do not filter".
   *
   * Put this straight into the request params. It is what turns the control's
   * `mine` into the signed-in user's id — the server is never told about "mine"
   * as a word, so that two screens cannot come to disagree about what it means.
   */
  param: string | undefined;
  /** Is the filter narrowing anything? Drives the "filtered" affordances. */
  active: boolean;
  /** The signed-in user's id, for rendering their own bubble as theirs. */
  meId: string | undefined;
}

/**
 * The Mine / Everyone / a-person filter, remembered per screen.
 *
 * **Why it is a browser preference and not a saved setting.** It is a lens on a
 * list, not data: nothing downstream reads it, nobody else can see it, and it
 * changes several times an hour. `UserSettings` holds the column arrangements
 * because those are a considered layout worth carrying between machines; which
 * way a filter was pointing twenty minutes ago is not, and putting it there
 * would mean a migration and a write on every toggle.
 *
 * **Why it opens on Everyone.** Every order and container that exists today has
 * no handler, so opening on Mine would greet all twelve dispatchers with an
 * empty screen on the morning this ships. Everyone needs no backfill and no
 * bulk assignment — the data fills in as orders are worked — and the moment
 * somebody chooses Mine, that is what their screen opens on from then on.
 */
export function useHandlerScope(
  screen: HandlerScopeScreen,
  enabled = true,
): UseHandlerScope {
  const [scope, setScopeState] = useState<HandlerScope>(() =>
    enabled ? readStored(screen) : DEFAULT_HANDLER_SCOPE,
  );
  const { meId } = useHandlers(enabled);

  const setScope = useCallback(
    (next: HandlerScope) => {
      setScopeState(next);
      try {
        localStorage.setItem(storageKey(screen), next);
      } catch {
        // The filter still works for this session; only the memory of it is
        // lost, which is not worth telling anybody about.
      }
    },
    [screen],
  );

  /**
   * "Mine" with nobody signed in resolves to no filter at all.
   *
   * It cannot happen on a rendered screen, but `handlerScopeParam` has to
   * answer for it, and answering "filter by the empty string" would be a
   * request for every record whose handler is nothing — the opposite of the
   * everything the control is showing as selected.
   */
  const param = handlerScopeParam(scope, meId);
  const active = param !== undefined;

  return { scope, setScope, param, active, meId };
}
